'use client';

import { PopImage } from '@ps/ui/atoms/pop-image';
import { ArrowLeft } from 'lucide-react';

import { useChatContext } from '../hooks/useChatContext';
import type { ChatSessionType } from '../types/ChatSessionType';
import { ChatSessionTypeTabBar } from './ChatSessionTypeTabBar';

interface Props {
  onBack?: () => void;
  /** When set, render the Private / Group tabs under the title and switch on select. */
  onSessionTypeChange?: (sessionType: ChatSessionType) => void;
}

/**
 * Header for the chat detail — back button (mobile / when provided),
 * target user's avatar + name, and a join-state indicator while the
 * server hasn't acknowledged the room enter yet. In the transient visitor
 * window it also carries the Private / Group session-type tabs.
 */
export function ChatRoomNavHeader({ onBack, onSessionTypeChange }: Props) {
  const { targetUser, joinState, backButton, sessionType } = useChatContext();
  const showBack = (backButton ?? true) && onBack != null;

  return (
    <header className="border-line-strong/80 [[data-theme=visitor]_&]:border-line border-b">
      <div className="flex items-center gap-3 px-4 py-3">
        {showBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            className="hover:bg-surface-1 text-content-1 -m-2 rounded-full p-2 transition"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden />
          </button>
        ) : null}
        <Avatar name={targetUser.name} src={targetUser.avatar} />
        <div className="min-w-0 flex-1">
          <p className="text-content-1 truncate text-sm font-semibold">
            {targetUser.name || 'Chat'}
          </p>
          {joinState.status !== 'joined' ? (
            <p className="text-content-3 truncate text-[11px]">
              {joinState.status === 'joining'
                ? 'Connecting…'
                : 'Connection error'}
            </p>
          ) : null}
        </div>
      </div>
      {onSessionTypeChange ? (
        <ChatSessionTypeTabBar
          value={sessionType}
          onChange={onSessionTypeChange}
          className="px-4 pb-1"
        />
      ) : null}
    </header>
  );
}

function Avatar({ name, src }: { name: string; src?: string }) {
  if (src) {
    return (
      <span className="bg-surface-3 relative h-9 w-9 shrink-0 overflow-hidden rounded-full">
        <PopImage
          src={src}
          alt={name}
          width={36}
          height={36}
          resizing={{ width: 108, height: 108 }}
          resizeMode="cover"
          className="h-full w-full"
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="bg-surface-3 text-content-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
    >
      {(name.charAt(0) || '?').toUpperCase()}
    </span>
  );
}
